import type { FormData } from './types';
import { calculateAgeFromDateString } from './date-utils';

export type PregnancyLactationFieldName =
  | 'pregnancyTrimester'
  | 'prePregnancyWeightKg'
  | 'twinPregnancy'
  | 'lactationStatus'
  | 'weightLossGoal';

export interface PregnancyLactationValidationError {
  field: PregnancyLactationFieldName;
  message: string;
}

const REQUIRED_MESSAGE = 'This field is required';

const hasValue = (value?: string): boolean => {
  return typeof value === 'string' && value.trim().length > 0;
};

const hasPositiveNumber = (value?: string): boolean => {
  if (!hasValue(value)) return false;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
};

export const getPregnancyLactationValidationErrors = (
  formData: FormData
): PregnancyLactationValidationError[] => {
  const effectiveGenderForPregnancy =
    formData.gender === 'male' || formData.gender === 'female'
      ? formData.gender
      : formData.sexAtBirth;

  if (effectiveGenderForPregnancy !== 'female') {
    return [];
  }

  const age = calculateAgeFromDateString(formData.dateOfBirth);
  if (age !== null && age < 18) {
    return [];
  }

  const pregnancyStatus = String(formData.pregnancyStatus || '').trim();
  const isPregnant =
    pregnancyStatus === 'pregnant' ||
    pregnancyStatus === 'first' ||
    pregnancyStatus === 'second' ||
    pregnancyStatus === 'third';

  const errors: PregnancyLactationValidationError[] = [];

  if (!hasValue(formData.lactationStatus)) {
    errors.push({ field: 'lactationStatus', message: REQUIRED_MESSAGE });
  }

  if (isPregnant) {
    if (!hasValue(formData.pregnancyTrimester)) {
      errors.push({ field: 'pregnancyTrimester', message: REQUIRED_MESSAGE });
    }

    if (!hasPositiveNumber(formData.prePregnancyWeightKg)) {
      errors.push({
        field: 'prePregnancyWeightKg',
        message: hasValue(formData.prePregnancyWeightKg) ? 'Enter a valid weight' : REQUIRED_MESSAGE,
      });
    }

    if (!hasValue(formData.twinPregnancy)) {
      errors.push({ field: 'twinPregnancy', message: REQUIRED_MESSAGE });
    }
  }

  const isLactating = hasValue(formData.lactationStatus) && formData.lactationStatus !== 'none';

  if (isLactating && !isPregnant && !hasValue(formData.twinPregnancy)) {
    errors.push({ field: 'twinPregnancy', message: REQUIRED_MESSAGE });
  }

  if (isLactating && !hasValue(formData.weightLossGoal)) {
    errors.push({ field: 'weightLossGoal', message: REQUIRED_MESSAGE });
  }

  return errors;
};
